import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../lib/supabase';
import { Key, Edit2, Save, X, Plus, Trash2, RefreshCw, Search, ChevronLeft, Camera, Sparkles, CheckSquare, Square, Lightbulb, Zap } from 'lucide-react';
import { CostBreakdown } from './CostBreakdown';
import { componentLibraryService } from '../services/componentLibraryService';
import { useAuth } from '../contexts/AuthContext';
import { useAnnotationSaveContext } from '../contexts/AnnotationSaveContext';
import { getUniqueComponentName, normalizeComponentColors, POWER_COLOR } from '../utils/componentNameUtils';
import { STANDARD_RECEPTACLE_CLASSES, LEGACY_STANDARD_RECEPTACLE_NAMES } from '../utils/receptacleUtils';
import { fetchAnnotationCountMapsV2 } from '../lib/annotationV2Persistence';
import '../styles/ProjectKeyPanel.css';

interface ComponentKey {
  color: string;
  customColor?: boolean;
  name: string;
  symbol: string;
  pages: Array<{ page: number; count: number }>;
  totalCount: number;
  referenceImage?: string;  // Optional for backward compatibility
  hasReferenceImage?: boolean;  // Flag indicating if image is in separate table
  description?: string;  // Full description (e.g. from lighting fixture schedule)
  symbolType?: string;  // Category: 'Lighting', 'Power', etc. (controls routing for annotations/autocount)
  componentType?: string;  // Classification: 'Switches', 'Receptacles', 'Distribution Equipment', 'Telecom', 'Lighting', 'Fire Alarm', 'Low Voltage', 'Other'
  subscript?: string;  // Subscript/suffix/variant indicator (e.g., '3' from S₃, 'WP' from SWP, '' if none)
  isStandardClass?: boolean; // Flag for auto-added standard components
}

interface ProjectKey {
  components: ComponentKey[];
  extractedAt: string;
  pdfHash?: string;
}

interface ProjectKeyPanelProps {
  estimationId: string;
  onComponentSelect?: (component: ComponentKey) => void;
  onKeyUpdate?: (key: ProjectKey) => void;
  pdfAnnotatorRef?: React.RefObject<any>;
  currentFile?: any;
  onCollapse?: () => void;
  onAnnotationCountsChange?: (counts: Map<string, Map<number, number>>) => void;
  filteredComponent?: string | null;
  onFilteredComponentChange?: (componentName: string | null) => void;
  extractionProgress?: string;
  onStartExtraction?: () => void;
  hideExtraction?: boolean;
  selectedForPlacement?: string | null;
  onComponentSelectForPlacement?: (component: { name: string; symbol: string; color: string; symbolType?: string } | null) => void;
  refreshTrigger?: number;  // Increment to trigger a count refresh
  filterSymbolType?: string; // Filter to only show components with this symbolType (e.g. 'Lighting')
}

// Color Wheel Component
const ColorWheel: React.FC<{
  currentColor: string;
  onColorChange: (color: string) => void;
  onClose: () => void;
}> = ({ currentColor, onColorChange, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Draw color wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    
    // Draw color wheel with vibrant colors at the edges
    for (let angle = 0; angle < 360; angle += 1) {
      const startAngle = (angle - 1) * Math.PI / 180;
      const endAngle = angle * Math.PI / 180;
      
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, `hsl(${angle}, 0%, 50%)`);     // Center: Gray (neutral)
      gradient.addColorStop(0.3, `hsl(${angle}, 50%, 50%)`);  // 30% out: Medium saturation
      gradient.addColorStop(0.7, `hsl(${angle}, 85%, 50%)`);  // 70% out: High saturation
      gradient.addColorStop(0.9, `hsl(${angle}, 100%, 50%)`); // 90% out: Full saturation
      gradient.addColorStop(1, `hsl(${angle}, 100%, 60%)`);   // Edge: SUPER BRIGHT & VIBRANT
      
      ctx.fillStyle = gradient;
      ctx.fill();
    }
    
    // Draw current color indicator
    const drawIndicator = () => {
      // Parse current color to find position
      let hue = 0;
      let saturation = 100;
      let lightness = 50;
      
      // Try to parse RGB color
      const rgbMatch = currentColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1]) / 255;
        const g = parseInt(rgbMatch[2]) / 255;
        const b = parseInt(rgbMatch[3]) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;
        
        if (delta !== 0) {
          if (max === r) {
            hue = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
          } else if (max === g) {
            hue = ((b - r) / delta + 2) * 60;
          } else {
            hue = ((r - g) / delta + 4) * 60;
          }
        }
        
        lightness = (max + min) / 2;
        saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
        saturation *= 100;
        lightness *= 100;
      }
      
      // Also handle hex colors
      const hexMatch = currentColor.match(/^#([A-Fa-f0-9]{6})$/);
      if (hexMatch) {
        const r = parseInt(hexMatch[1].substring(0, 2), 16) / 255;
        const g = parseInt(hexMatch[1].substring(2, 4), 16) / 255;
        const b = parseInt(hexMatch[1].substring(4, 6), 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;
        
        if (delta !== 0) {
          if (max === r) {
            hue = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
          } else if (max === g) {
            hue = ((b - r) / delta + 2) * 60;
          } else {
            hue = ((r - g) / delta + 4) * 60;
          }
        }
        
        lightness = (max + min) / 2;
        saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
        saturation *= 100;
        lightness *= 100;
      }
      
      // Calculate position
      const angle = hue * Math.PI / 180;
      const distance = (saturation / 100) * radius * 0.9;
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance;
      
      // Draw indicator
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.stroke();
      
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.stroke();
    };
    
    drawIndicator();
  }, [currentColor]);
  
  const handleColorSelect = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Calculate angle and distance
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxRadius = Math.min(centerX, centerY) - 10;
    
    if (distance <= maxRadius) {
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle < 0) angle += 360;
      
      // Match the gradient: increase saturation and brightness toward edges
      const saturation = Math.min(100, (distance / maxRadius) * 100);
      const lightness = 50 + (distance / maxRadius) * 10; // Gets brighter toward edge (50% to 60%)
      
      // Convert HSL to RGB
      const h = angle;
      const s = saturation / 100;
      const l = lightness / 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x1 = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = l - c / 2;
      
      let r = 0, g = 0, b = 0;
      
      if (h >= 0 && h < 60) {
        r = c; g = x1; b = 0;
      } else if (h >= 60 && h < 120) {
        r = x1; g = c; b = 0;
      } else if (h >= 120 && h < 180) {
        r = 0; g = c; b = x1;
      } else if (h >= 180 && h < 240) {
        r = 0; g = x1; b = c;
      } else if (h >= 240 && h < 300) {
        r = x1; g = 0; b = c;
      } else {
        r = c; g = 0; b = x1;
      }
      
      r = Math.round((r + m) * 255);
      g = Math.round((g + m) * 255);
      b = Math.round((b + m) * 255);
      
      const color = `rgb(${r}, ${g}, ${b})`;
      onColorChange(color);
    }
  };
  
  return (
    <div className="color-picker-popup">
      <div className="color-picker-header">
        <span>Select Color</span>
        <button className="color-picker-close" onClick={onClose}>×</button>
      </div>
      <canvas
        ref={canvasRef}
        width={150}
        height={150}
        className="color-wheel-canvas"
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onMouseMove={(e) => isDragging && handleColorSelect(e)}
        onClick={handleColorSelect}
      />
      <div className="color-preview" style={{ backgroundColor: currentColor }}>
        <span>{currentColor}</span>
      </div>
    </div>
  );
};

// Default Switch Reference Image (White background with "$")
const DEFAULT_SWITCH_IMAGE = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ3aGl0ZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iNjAiIGZpbGw9ImJsYWNrIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjM1ZW0iPiQ8L3RleHQ+PC9zdmc+";

export const ProjectKeyPanel: React.FC<ProjectKeyPanelProps> = ({
  estimationId,
  onComponentSelect,
  onKeyUpdate,
  pdfAnnotatorRef,
  currentFile,
  onCollapse,
  onAnnotationCountsChange,
  filteredComponent,
  onFilteredComponentChange,
  extractionProgress: externalProgress,
  onStartExtraction,
  hideExtraction = false,
  selectedForPlacement,
  onComponentSelectForPlacement,
  refreshTrigger,
  filterSymbolType
}) => {
  const { user } = useAuth();
  const { acquireSaveLock, releaseSaveLock } = useAnnotationSaveContext();
  const [projectKey, setProjectKey] = useState<ProjectKey | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedComponent, setEditedComponent] = useState<ComponentKey | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [highlightedComponentName, setHighlightedComponentName] = useState<string | null>(null);
  const [annotationCounts, setAnnotationCounts] = useState<Map<string, Map<number, number>>>(new Map());
  const [isRecounting, setIsRecounting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColorPicker, setShowColorPicker] = useState<'edit' | 'new' | 'inline' | null>(null);
  const [inlineColorPickerIndex, setInlineColorPickerIndex] = useState<number | null>(null);
  const [inlinePreviewColor, setInlinePreviewColor] = useState<string | null>(null);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [isExtractingKey, setIsExtractingKey] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const editNameRef = useRef<HTMLTextAreaElement>(null);
  const editSymbolRef = useRef<HTMLTextAreaElement>(null);
  const [newComponent, setNewComponent] = useState<ComponentKey>({
    color: 'rgb(255, 0, 0)',
    name: '',
    symbol: '',
    pages: [],
    totalCount: 0
  });
  const [componentImages, setComponentImages] = useState<Map<string, string>>(new Map());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [projectName, setProjectName] = useState<string>('');
  const [isAutocounting, setIsAutocounting] = useState(false);
  const [autocountProgress, setAutocountProgress] = useState('');
  const [selectedComponents, setSelectedComponents] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  
  // Use ref to track current key for comparison (avoids stale closure in interval)
  const projectKeyRef = useRef<ProjectKey | null>(null);

  // Track current page from PDFAnnotator
  useEffect(() => {
    const interval = setInterval(() => {
      if (pdfAnnotatorRef?.current?.currentPage !== undefined) {
        setCurrentPage(pdfAnnotatorRef.current.currentPage);
      }
    }, 100); // Check every 100ms for page changes

    return () => clearInterval(interval);
  }, [pdfAnnotatorRef]);

  // Handle click outside to clear highlight
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Keep component filter persistent - don't clear on outside click
      // Only clear the local highlight visual state
      if (!target.closest('.component-card') && !target.closest('.project-key-panel') && highlightedComponentName) {
        setHighlightedComponentName(null);
        // DON'T clear the filter in PDFAnnotator - keep it active
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [highlightedComponentName, pdfAnnotatorRef]);

  // Close color picker on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(null);
        setInlineColorPickerIndex(null);
        setInlinePreviewColor(null);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showColorPicker]);

  // Color palette for new components
  const COLOR_PALETTE = [
    '#FF0000', '#0080FF', '#00FF00', '#FFD700', '#FF00FF',
    '#00FFFF', '#FF6600', '#FF1493', '#7FFF00', '#FF69B4'
  ];

  // Keep ref in sync with state
  useEffect(() => {
    projectKeyRef.current = projectKey;
  }, [projectKey]);

  // Notify parent when annotation counts change
  useEffect(() => {
    if (onAnnotationCountsChange) {
      onAnnotationCountsChange(annotationCounts);
    }
  }, [annotationCounts, onAnnotationCountsChange]);

  // Fetch actual annotation counts from the database
  const fetchAnnotationCounts = useCallback(async () => {
    setIsRecounting(true);
    try {
      const counts = await fetchAnnotationCountMapsV2({
        supabase: supabase as any,
        estimationId,
      });
      setAnnotationCounts(counts);
      setIsRecounting(false);
    } catch (error) {
      console.error('Error fetching annotation counts:', error);
      setIsRecounting(false);
    }
  }, [estimationId]);


  // Load component images from key metadata (stored inline in project_key)
  const loadComponentImages = (key?: ProjectKey | null) => {
    const keyToUse = key || projectKeyRef.current;
    if (!keyToUse?.components) return;
    
    const images = new Map<string, string>();
    for (const component of keyToUse.components) {
      if (component.referenceImage) {
        images.set(component.name, component.referenceImage);
      }
    }
    setComponentImages(images);
    console.log(`📸 Loaded ${images.size} component reference images from key metadata`);
  };

  const loadProjectKey = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('estimations')
        .select('project_key, name')
        .eq('id', estimationId)
        .single();

      if (error) {
        console.error('Error loading project key:', error);
        return;
      }

      if (data) {
        // Set project name
        if (data.name) {
          setProjectName(data.name);
        }

        // Set project key if it exists
        if (data.project_key) {
          // Only update if the key has actually changed (use ref for comparison)
          const newKeyString = JSON.stringify(data.project_key);
          const currentKeyString = JSON.stringify(projectKeyRef.current);
          
          if (newKeyString !== currentKeyString) {
            console.log('🔄 Project key changed, updating...');
            // Normalize colors: Power=green, Lighting=gold (backwards compat)
            let normalizedComponents = normalizeComponentColors(data.project_key.components || []);
            
            // Ensure default Switch exists if no switches are present
            const hasSwitches = normalizedComponents.some((c: any) => 
              (c.componentType === 'Switches') || 
              (((c.name || '').toLowerCase().includes('switch') && !(c.name || '').toLowerCase().includes('receptacle')) || 
              (c.name || '').toLowerCase().includes('dimmer'))
            );
            
            if (!hasSwitches) {
              console.log('🔌 No switches found, adding default Switch component');
              normalizedComponents.push({
                name: 'Switch',
                symbol: '$',
                color: '#10b981',
                description: 'Standard wall-mounted switch',
                referenceImage: DEFAULT_SWITCH_IMAGE,
                symbolType: 'Power',
                componentType: 'Switches',
                isStandardClass: true,
                pages: [],
                totalCount: 0
              });
            }

            // --- Standard Receptacle Class Migration ---
            const legacyNames = new Set(LEGACY_STANDARD_RECEPTACLE_NAMES);
            const hasAnyReceptacles = normalizedComponents.some(c => 
              c.componentType === 'Receptacles' || (c.name || '').toLowerCase().includes('receptacle')
            );

            if (hasAnyReceptacles) {
              // 1. Remove legacy standard classes
              const beforeCount = normalizedComponents.length;
              normalizedComponents = normalizedComponents.filter(c => 
                !(c.isStandardClass && legacyNames.has(c.name || ''))
              );
              if (normalizedComponents.length < beforeCount) {
                console.log(`🧹 Removed ${beforeCount - normalizedComponents.length} legacy standard receptacle classes`);
              }

              // 2. Auto-add missing standard classes
              const existingStandardNames = new Set(
                normalizedComponents.filter(c => c.isStandardClass).map(c => c.name)
              );
              
              // Find a reference image to use for new standard classes
              const refImage = normalizedComponents.find(c => 
                c.componentType === 'Receptacles' && c.referenceImage && !c.isStandardClass
              )?.referenceImage || normalizedComponents.find(c => 
                c.componentType === 'Receptacles' && c.referenceImage
              )?.referenceImage;

              const newStandards: any[] = [];
              for (const [key, name] of Object.entries(STANDARD_RECEPTACLE_CLASSES)) {
                if (!existingStandardNames.has(name)) {
                  newStandards.push({
                    color: POWER_COLOR,
                    name: name,
                    symbol: name,
                    componentType: 'Receptacles',
                    description: `Auto-generated standard class for ${key.replace(/__/g, ' + ').replace(/_/g, ' ')}`,
                    subscript: '',
                    pages: [],
                    totalCount: 0,
                    referenceImage: refImage || '',
                    hasReferenceImage: !!refImage,
                    isStandardClass: true,
                  });
                }
              }

              if (newStandards.length > 0) {
                console.log(`⚡ Auto-adding ${newStandards.length} missing standard receptacle classes`);
                normalizedComponents.push(...newStandards);
              }
            }
            // ------------------------------------------

            const normalizedKey = {
              ...data.project_key,
              components: normalizedComponents
            };
            projectKeyRef.current = normalizedKey;
            setProjectKey(normalizedKey);
            // Also load component images from key metadata when key changes
            loadComponentImages(normalizedKey);
          } else {
            console.log('✅ Project key unchanged, skipping update');
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [estimationId]);

  const saveProjectKey = async (updatedKey: ProjectKey, skipCountUpdate = false) => {
    try {
      // Don't save counts to the database - they should come from actual annotations
      const keyToSave = skipCountUpdate ? updatedKey : {
        ...updatedKey,
        components: updatedKey.components.map(c => ({
          ...c,
          pages: [],  // Clear pages to avoid saving stale counts
          totalCount: 0  // Clear total count
        }))
      };

      const { error } = await supabase
        .from('estimations')
        .update({ project_key: keyToSave })
        .eq('id', estimationId);

      if (error) {
        console.error('Error saving project key:', error);
        return;
      }

      // Keep the full key with counts in local state
      setProjectKey(updatedKey);
      if (onKeyUpdate) {
        onKeyUpdate(updatedKey);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Load project key from Supabase and fetch counts
  useEffect(() => {
    const loadKeyAndCounts = async () => {
      await loadProjectKey();
      await fetchAnnotationCounts();
    };
    loadKeyAndCounts();
  }, [estimationId, fetchAnnotationCounts]);

  // Removed periodic refresh - only update via real-time subscription
  
  // Refresh key AND counts when refreshTrigger changes (triggered by parent after annotation save or key extraction)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      console.log('🔄 Refresh triggered by parent, fetching project key and annotation counts...');
      loadProjectKey();
      fetchAnnotationCounts();
    }
  }, [refreshTrigger, fetchAnnotationCounts, loadProjectKey]);

  // Set up real-time subscription for annotation changes
  useEffect(() => {
    if (!estimationId) return;

    const subscription = supabase
      .channel(`annotations-changes-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'annotation_entities_v2',
          filter: `estimation_id=eq.${estimationId}`
        },
        (payload) => {
          // Refresh counts and key when annotations change
          console.log('📊 Annotation change detected, refreshing electrical key...', payload);
          loadProjectKey();  // Reload the key to get new component types
          fetchAnnotationCounts();
        }
      )
      .subscribe((status) => {
        console.log('🔔 Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to annotation changes');
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [estimationId, fetchAnnotationCounts]);


  const handleEdit = (index: number) => {
    if (projectKey?.components[index].isStandardClass) {
      console.warn('🚫 Cannot edit standard component');
      return;
    }
    setEditingIndex(index);
    setEditedComponent({ ...projectKey!.components[index] });
  };

  // Auto-resize textareas when entering edit mode
  useEffect(() => {
    if (editingIndex !== null) {
      setTimeout(() => {
        if (editNameRef.current) {
          editNameRef.current.style.height = 'auto';
          editNameRef.current.style.height = editNameRef.current.scrollHeight + 'px';
        }
        if (editSymbolRef.current) {
          editSymbolRef.current.style.height = 'auto';
          editSymbolRef.current.style.height = editSymbolRef.current.scrollHeight + 'px';
        }
      }, 0);
    }
  }, [editingIndex]);

  const handleSave = async () => {
    if (!projectKey || editingIndex === null || !editedComponent) return;

    // Get the old component name before updating
    const oldComponent = projectKey.components[editingIndex];
    const nameChanged = oldComponent.name !== editedComponent.name;

    // If name changed, ensure the new name doesn't collide with other existing components
    let finalComponent = editedComponent;
    if (nameChanged) {
      const otherNames = projectKey.components
        .filter((_: any, i: number) => i !== editingIndex)
        .map((c: any) => c.name);
      const uniqueName = getUniqueComponentName(editedComponent.name, otherNames);
      if (uniqueName !== editedComponent.name) {
        finalComponent = { ...editedComponent, name: uniqueName };
      }
    }

    const updatedComponents = [...projectKey.components];
    updatedComponents[editingIndex] = finalComponent;

    const updatedKey = {
      ...projectKey,
      components: updatedComponents
    };

    await saveProjectKey(updatedKey);
    
    // If the component name changed, update existing annotations
    if (nameChanged && estimationId) {
      await acquireSaveLock();
      try {
        console.log(`🔄 Updating annotations from "${oldComponent.name}" to "${finalComponent.name}"`);
        
        const { error: updateError } = await supabase
          .from('annotation_entities_v2')
          .update({ component_type: finalComponent.name })
          .eq('estimation_id', estimationId)
          .eq('component_type', oldComponent.name);

        if (updateError) {
          console.error('Error updating annotations for component rename:', updateError);
        } else {
          console.log(`✅ Updated annotation rows from "${oldComponent.name}" to "${finalComponent.name}"`);
          
          // Refresh annotation counts after updating
          await fetchAnnotationCounts();
          
          // Force PDFAnnotator to reload annotations
          if (pdfAnnotatorRef?.current?.loadAnnotations) {
            console.log('🔄 Triggering PDFAnnotator to reload annotations...');
            await pdfAnnotatorRef.current.loadAnnotations();
          }
        }
      } catch (error) {
        console.error('Error updating annotations after name change:', error);
      } finally {
        releaseSaveLock();
      }
    }
    
    setEditingIndex(null);
    setEditedComponent(null);
    
    // Notify parent of key update
    if (onKeyUpdate) {
      onKeyUpdate(updatedKey);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedComponent(null);
  };

  const handleInlineColorConfirm = async () => {
    if (!projectKey || inlineColorPickerIndex === null || !inlinePreviewColor) return;
    const updatedComponents = [...projectKey.components];
    updatedComponents[inlineColorPickerIndex] = {
      ...updatedComponents[inlineColorPickerIndex],
      color: inlinePreviewColor,
      customColor: true
    };
    const updatedKey = { ...projectKey, components: updatedComponents };
    setProjectKey(updatedKey);
    setShowColorPicker(null);
    setInlineColorPickerIndex(null);
    setInlinePreviewColor(null);
    await saveProjectKey(updatedKey);
    if (onKeyUpdate) {
      onKeyUpdate(updatedKey);
    }
  };

  const handleInlineColorDismiss = () => {
    setShowColorPicker(null);
    setInlineColorPickerIndex(null);
    setInlinePreviewColor(null);
  };

  const handleDelete = async (index: number) => {
    if (!projectKey) return;

    const componentToDelete = projectKey.components[index];
    if (componentToDelete.isStandardClass) {
      console.warn('🚫 Cannot delete standard component');
      return;
    }
    
    // First, delete all annotations for this component type
    await acquireSaveLock();
    try {
      console.log(`🗑️ Deleting annotations for component: "${componentToDelete.name}"`);
      const { error } = await supabase
        .from('annotation_entities_v2')
        .delete()
        .eq('estimation_id', estimationId)
        .eq('component_type', componentToDelete.name);
      
      if (error) {
        console.error('Error deleting annotations:', error);
      } else {
        console.log(`✅ Deleted annotations for "${componentToDelete.name}"`);
        
        // Reload annotations in PDFAnnotator to remove them from the canvas
        if (pdfAnnotatorRef?.current?.loadAnnotations) {
          console.log('🔄 Triggering PDFAnnotator to reload annotations...');
          await pdfAnnotatorRef.current.loadAnnotations();
        }
        
        // Refresh annotation counts
        await fetchAnnotationCounts();
      }
    } catch (error) {
      console.error('Failed to delete annotations:', error);
    } finally {
      releaseSaveLock();
    }

    // Then update the key
    const updatedComponents = projectKey.components.filter((_, i) => i !== index);
    const updatedKey = {
      ...projectKey,
      components: updatedComponents
    };

    await saveProjectKey(updatedKey);
    
    // Notify parent of key update
    if (onKeyUpdate) {
      onKeyUpdate(updatedKey);
    }
  };

  const toggleComponentSelection = (selectionKey: string) => {
    setSelectedComponents(prev => {
      const next = new Set(prev);
      if (next.has(selectionKey)) {
        next.delete(selectionKey);
      } else {
        next.add(selectionKey);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!projectKey || selectedComponents.size === 0) return;

    const confirmed = window.confirm(`Delete ${selectedComponents.size} component${selectedComponents.size > 1 ? 's' : ''} and all their annotations?`);
    if (!confirmed) return;

    setIsBulkDeleting(true);
    await acquireSaveLock();
    try {
      for (const selectionKey of Array.from(selectedComponents)) {
        const component = projectKey.components.find(c => {
          const key = c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name;
          return key === selectionKey;
        });
        if (!component || component.isStandardClass) continue;

        const { error } = await supabase
          .from('annotation_entities_v2')
          .delete()
          .eq('estimation_id', estimationId)
          .eq('component_type', component.name);

        if (error) {
          console.error(`Error deleting annotations for "${component.name}":`, error);
        }
      }

      if (pdfAnnotatorRef?.current?.loadAnnotations) {
        await pdfAnnotatorRef.current.loadAnnotations();
      }
      await fetchAnnotationCounts();

      const updatedComponents = projectKey.components.filter(c => {
        const key = c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name;
        return !selectedComponents.has(key);
      });
      const updatedKey = { ...projectKey, components: updatedComponents };
      await saveProjectKey(updatedKey);

      if (onKeyUpdate) {
        onKeyUpdate(updatedKey);
      }

      setSelectedComponents(new Set());
    } catch (error) {
      console.error('Bulk delete failed:', error);
    } finally {
      releaseSaveLock();
      setIsBulkDeleting(false);
    }
  };

  const handleAddNew = async () => {
    if (!projectKey || !newComponent.name || !newComponent.symbol) return;

    // Assign next available color
    const usedColors = projectKey.components.map(c => c.color);
    const availableColor = COLOR_PALETTE.find(c => !usedColors.includes(c)) ||
                          COLOR_PALETTE[projectKey.components.length % COLOR_PALETTE.length];

    const LIGHTING_GOLD = 'rgba(245, 158, 11, 0.6)';
    const componentToAdd = {
      ...newComponent,
      color: filterSymbolType === 'Lighting' ? LIGHTING_GOLD : availableColor,
      name: newComponent.name.toLowerCase().trim(),
      ...(filterSymbolType ? { symbolType: filterSymbolType } : {})
    };

    const updatedKey = {
      ...projectKey,
      components: [...projectKey.components, componentToAdd]
    };

    await saveProjectKey(updatedKey);
    
    // NEW: Sync to rolling key library
    if (user?.id) {
      await componentLibraryService.syncComponentToLibrary(user.id, {
        user_id: user.id,
        component_name: componentToAdd.name,
        symbol: componentToAdd.symbol,
        color: componentToAdd.color
      });
    }
    
    setIsAddingNew(false);
    setNewComponent({
      color: 'rgb(255, 0, 0)',
      name: '',
      symbol: '',
      pages: [],
      totalCount: 0
    });
    
    // Notify parent of key update
    if (onKeyUpdate) {
      onKeyUpdate(updatedKey);
    }
  };

  // Sync external progress with local state
  useEffect(() => {
    if (externalProgress) {
      setExtractionProgress(externalProgress);
      // Mark as extracting if we have progress
      if (!isExtractingKey && externalProgress) {
        setIsExtractingKey(true);
      }
      // Clear extracting state and reload key when extraction completes
      if (externalProgress.includes('Extracted') && externalProgress.includes('symbols')) {
        setTimeout(() => {
          setIsExtractingKey(false);
          setExtractionProgress('');
          // Reload the key to show new data
          loadProjectKey();
        }, 2000);
      } else if (externalProgress.includes('❌')) {
        setTimeout(() => {
          setIsExtractingKey(false);
          setExtractionProgress('');
        }, 3000);
      }
    }
  }, [externalProgress, loadProjectKey, isExtractingKey]);

  // Handle selecting key from PDF
  const handleSelectKeyFromPDF = () => {
    // Set extracting state
    setIsExtractingKey(true);
    setExtractionProgress('Select the key table area...');
    
    // Notify parent to enable auto-extract mode
    if (onStartExtraction) {
      onStartExtraction();
    }
    
    // Activate snapshot tool in PDFAnnotator
    if (pdfAnnotatorRef?.current?.setCurrentTool) {
      pdfAnnotatorRef.current.setCurrentTool('snapshot');
    }
  };

  // NMS (Non-Maximum Suppression) - Remove duplicate detections
  const nonMaximumSuppression = (predictions: any[], iouThreshold = 0.5) => {
    if (predictions.length === 0) return [];
    
    // Sort by confidence (descending)
    const sorted = [...predictions].sort((a, b) => b.confidence - a.confidence);
    const keep = [];
    
    while (sorted.length > 0) {
      const current = sorted.shift()!;
      keep.push(current);
      
      // Remove overlapping boxes
      const remaining = [];
      for (const pred of sorted) {
        const iou = calculateIoU(current, pred);
        if (iou < iouThreshold) {
          remaining.push(pred);
        }
      }
      sorted.length = 0;
      sorted.push(...remaining);
    }
    
    return keep;
  };

  // Calculate IoU (Intersection over Union)
  const calculateIoU = (box1: any, box2: any) => {
    const box1_x1 = box1.x - box1.width / 2;
    const box1_y1 = box1.y - box1.height / 2;
    const box1_x2 = box1.x + box1.width / 2;
    const box1_y2 = box1.y + box1.height / 2;
    
    const box2_x1 = box2.x - box2.width / 2;
    const box2_y1 = box2.y - box2.height / 2;
    const box2_x2 = box2.x + box2.width / 2;
    const box2_y2 = box2.y + box2.height / 2;
    
    const x1 = Math.max(box1_x1, box2_x1);
    const y1 = Math.max(box1_y1, box2_y1);
    const x2 = Math.min(box1_x2, box2_x2);
    const y2 = Math.min(box1_y2, box2_y2);
    
    const intersectionArea = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const box1Area = box1.width * box1.height;
    const box2Area = box2.width * box2.height;
    const unionArea = box1Area + box2Area - intersectionArea;
    
    return intersectionArea / unionArea;
  };

  // Slice canvas into tiles
  const sliceCanvasIntoTiles = async (canvas: HTMLCanvasElement, gridSize = 5) => {
    const tileWidth = Math.floor(canvas.width / gridSize);
    const tileHeight = Math.floor(canvas.height / gridSize);
    const tiles = [];
    
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const tileCanvas = document.createElement('canvas');
        const left = col * tileWidth;
        const top = row * tileHeight;
        
        // Handle last row/column
        tileCanvas.width = (col === gridSize - 1) ? canvas.width - left : tileWidth;
        tileCanvas.height = (row === gridSize - 1) ? canvas.height - top : tileHeight;
        
        const ctx = tileCanvas.getContext('2d');
        if (!ctx) continue;
        
        // Draw tile from main canvas
        ctx.drawImage(
          canvas,
          left, top, tileCanvas.width, tileCanvas.height,
          0, 0, tileCanvas.width, tileCanvas.height
        );
        
        // Convert to base64 JPEG (smaller)
        const base64 = tileCanvas.toDataURL('image/jpeg', 0.85).split(',')[1];
        
        tiles.push({
          base64,
          xOffset: left,
          yOffset: top,
          width: tileCanvas.width,
          height: tileCanvas.height,
          row,
          col,
          index: row * gridSize + col
        });
      }
    }
    
    return tiles;
  };

  // Handle autocount receptacles
  const handleAutocount = async () => {
    if (!pdfAnnotatorRef?.current?.getCanvasScreenshot) {
      console.error('❌ PDFAnnotator screenshot method not available');
      return;
    }

    setIsAutocounting(true);
    setAutocountProgress('Preparing canvas...');

    try {
      // Get the actual canvas element (not base64)
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      
      if (!pdfCanvas) {
        throw new Error('PDF canvas not found');
      }

      const canvasWidth = pdfCanvas.width;
      const canvasHeight = pdfCanvas.height;

      console.log('📐 Canvas dimensions:', { width: canvasWidth, height: canvasHeight });

      setAutocountProgress('Slicing image into tiles...');

      // Slice canvas into 5x5 tiles
      const tiles = await sliceCanvasIntoTiles(pdfCanvas, 5);
      console.log(`✂️ Created ${tiles.length} tiles`);

      setAutocountProgress('Analyzing with AI (0/25)...');

      // Process tiles in parallel batches (5 at a time for speed)
      const allPredictions: any[] = [];
      const BATCH_SIZE = 5;
      
      for (let batchStart = 0; batchStart < tiles.length; batchStart += BATCH_SIZE) {
        const batch = tiles.slice(batchStart, batchStart + BATCH_SIZE);
        const batchNum = Math.floor(batchStart / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(tiles.length / BATCH_SIZE);
        
        setAutocountProgress(`Analyzing batch ${batchNum}/${totalBatches} (tiles ${batchStart + 1}-${Math.min(batchStart + BATCH_SIZE, tiles.length)})...`);

        // Process batch in parallel
        const batchPromises = batch.map(async (tile) => {
          try {
            const response = await fetch('/api/autocount-receptacles', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                tileBase64: tile.base64,
                tileIndex: tile.index
              })
            });

            if (!response.ok) {
              console.error(`⚠️ Tile ${tile.index} failed: ${response.statusText}`);
              return { predictions: [] };
            }

            const result = await response.json();

            if (result.success && result.predictions && result.predictions.length > 0) {
              // Adjust coordinates to full image
              const adjustedPredictions = result.predictions.map((pred: any) => ({
                ...pred,
                x: pred.x + tile.xOffset,
                y: pred.y + tile.yOffset
              }));
              
              console.log(`✓ Tile ${tile.index}: Found ${result.predictions.length} detections`);
              return { predictions: adjustedPredictions };
            }
            
            return { predictions: [] };
          } catch (tileError) {
            console.error(`❌ Error processing tile ${tile.index}:`, tileError);
            return { predictions: [] };
          }
        });

        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);
        
        // Collect predictions from this batch
        batchResults.forEach(result => {
          allPredictions.push(...result.predictions);
        });
      }

      console.log(`✂️ Total detections before NMS: ${allPredictions.length}`);

      setAutocountProgress('Removing duplicates...');

      // Run NMS to remove duplicates
      const finalPredictions = nonMaximumSuppression(allPredictions, 0.5);
      console.log(`✅ After NMS: ${finalPredictions.length} detections`);

      setAutocountProgress(`Found ${finalPredictions.length} receptacles! Saving...`);

      // Convert to annotations
      const detections = finalPredictions.map((pred: any, idx: number) => {
        // Use relative coordinates directly from the prediction if available.
        const relX = pred.relX !== undefined ? pred.relX : pred.x / canvasWidth;
        const relY = pred.relY !== undefined ? pred.relY : pred.y / canvasHeight;
        const relWidth = pred.relWidth !== undefined ? pred.relWidth : (pred.width || 40) / canvasWidth;
        const relHeight = pred.relHeight !== undefined ? pred.relHeight : (pred.height || 40) / canvasHeight;
        
        const relStartX = relX - relWidth / 2;
        const relStartY = relY - relHeight / 2;

        return {
          id: `auto-${Date.now()}-${idx}`,
          type: 'circle',
          component_type: 'RECEPTACLES',
          page: currentPage,
          number: idx + 1,
          // Store normalized coordinates
          relativeX: relX,
          relativeY: relY,
          relativeStartX: relStartX,
          relativeStartY: relStartY,
          // Absolute values
          x: relX * canvasWidth,
          y: relY * canvasHeight,
          startX: relStartX * canvasWidth,
          startY: relStartY * canvasHeight,
          radius: 8,
          width: relWidth * canvasWidth,
          height: relHeight * canvasHeight,
          color: 'var(--cyan-text-dark)',
          thickness: 2,
          confidence: pred.confidence,
          auto_detected: true,
          timestamp: new Date().toISOString(),
          savedCanvasWidth: canvasWidth,
          savedCanvasHeight: canvasHeight
        };
      });

      console.log('💾 Saving autocount detections:', {
        count: detections.length,
        currentFile: currentFile,
        fileId: currentFile?.id,
        estimationId: estimationId
      });

      // Save detections as row-level v2 annotations (global scope for receptacles)
      await acquireSaveLock();
      try {
        // Clear prior auto-detected receptacles for this page in global scope.
        const { error: deleteError } = await supabase
          .from('annotation_entities_v2')
          .delete()
          .eq('estimation_id', estimationId)
          .eq('scope_type', 'global')
          .is('file_id', null)
          .eq('component_type', 'RECEPTACLES')
          .eq('page_number', currentPage)
          .filter('meta->>auto_detected', 'eq', 'true');
        if (deleteError) throw deleteError;

        const rows = detections.map((d: any) => ({
          annotation_uid: String(d.id),
          estimation_id: estimationId,
          scope_type: 'global',
          file_id: null,
          page_number: currentPage,
          component_type: 'RECEPTACLES',
          annotation_type: d.type || 'circle',
          geometry: {
            x: d.x,
            y: d.y,
            startX: d.startX,
            startY: d.startY,
            width: d.width,
            height: d.height,
            radius: d.radius,
            relativeX: d.relativeX,
            relativeY: d.relativeY,
            relativeStartX: d.relativeStartX,
            relativeStartY: d.relativeStartY,
            savedCanvasWidth: d.savedCanvasWidth,
            savedCanvasHeight: d.savedCanvasHeight
          },
          style: {
            color: d.color,
            thickness: d.thickness,
            number: d.number
          },
          meta: {
            auto_detected: Boolean(d.auto_detected),
            confidence: d.confidence ?? null,
            timestamp: d.timestamp ?? new Date().toISOString()
          }
        }));

        if (rows.length > 0) {
          const { error: insertError } = await supabase
            .from('annotation_entities_v2')
            .insert(rows);
          if (insertError) throw insertError;
        }

        console.log(`✅ Saved ${rows.length} receptacle detections into annotation_entities_v2`);

      setAutocountProgress('✅ Complete! Displaying...');
      
      // Small delay to ensure database write is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload annotations in PDFAnnotator to display them
      if (pdfAnnotatorRef?.current?.loadAnnotations) {
        console.log('🔄 Reloading annotations to display detections...');
        await pdfAnnotatorRef.current.loadAnnotations();
      }
      
      // Refresh annotation counts
      await fetchAnnotationCounts();

      setTimeout(() => {
        setIsAutocounting(false);
        setAutocountProgress('');
      }, 2000);

      } finally {
        releaseSaveLock();
      }

    } catch (error) {
      console.error('❌ Autocount error:', error);
      setAutocountProgress(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      setTimeout(() => {
        setIsAutocounting(false);
        setAutocountProgress('');
      }, 3000);
    }
  };

  // Handle Count All for a folder
  const handleCountAll = async (groupId: string, items: ComponentKey[]) => {
    if (!pdfAnnotatorRef?.current?.startCountingComponents) {
      console.error('❌ PDFAnnotator startCountingComponents not available');
      return;
    }

    console.log(`🚀 Starting Count All for group: ${groupId} (${items.length} items)`);

    // Filter items that have reference images
    const itemsWithImages = items.filter(item => item.referenceImage || item.symbolType === 'Lighting' || item.componentType === 'Switches');
    
    if (itemsWithImages.length === 0) {
      console.warn(`⚠️ No items in ${groupId} have reference images to count.`);
      return;
    }

    // Trigger the normal multi-count flow in PDFAnnotator
    pdfAnnotatorRef.current.startCountingComponents(itemsWithImages.map(item => ({
      color: item.color,
      name: item.name,
      symbol: item.symbol,
      referenceImage: item.referenceImage || '',
      symbolType: item.symbolType,
      componentType: item.componentType,
      subscript: item.subscript,
      description: item.description,
      isStandardClass: item.isStandardClass
    })));
  };

  const isJunctionBoxComponent = (component: ComponentKey) => {
    const n = (component.name || '').toLowerCase().replace(/[-_]/g, ' ');
    return n.includes('junction') || n.includes('jbox') || n.includes('j box') || (n.includes('j') && n.includes('box'));
  };

  const isSwitchComponent = (component: ComponentKey) => {
    if (component.componentType === 'Switches') return true;
    const n = (component.name || '').toLowerCase();
    if (n.includes('receptacle')) return false;
    return n.includes('switch') || n.includes('dimmer');
  };

  const handleCountAllPower = () => {
    if (!projectKey?.components || !pdfAnnotatorRef?.current?.startCountingComponentsParallel) {
      console.error('❌ PDFAnnotator startCountingComponentsParallel not available');
      return;
    }

    const hasAnyStandardFlag = projectKey.components.some((component: ComponentKey) => component.isStandardClass === true);
    const componentsToCount = projectKey.components.filter((component: ComponentKey) => {
      const isReceptacles = component.componentType === 'Receptacles';
      const isTelecom = component.componentType === 'Telecom';
      const isBoxes = isJunctionBoxComponent(component);
      const isSwitches = isSwitchComponent(component);
      const isPowerStandard = isReceptacles || isTelecom || isBoxes || isSwitches;
      const canCount = !!component.referenceImage || component.symbolType === 'Lighting' || component.componentType === 'Switches' || isSwitches;
      const isEligibleStandard = hasAnyStandardFlag ? component.isStandardClass === true : true;
      return isPowerStandard && isEligibleStandard && canCount;
    });

    if (componentsToCount.length === 0) {
      console.warn('⚠️ No standard power components available for Count All Power (check standard key components and reference images)');
      return;
    }

    console.log(`🚀 Starting Count All Power for ${componentsToCount.length} standard components`);
    pdfAnnotatorRef.current.startCountingComponentsParallel(componentsToCount.map((item: ComponentKey) => ({
      color: item.color,
      name: item.name,
      symbol: item.symbol,
      referenceImage: item.referenceImage || '',
      symbolType: item.symbolType,
      componentType: item.componentType,
      subscript: item.subscript,
      description: item.description,
      isStandardClass: item.isStandardClass
    })));
  };

  if (!projectKey) {
    return (
      <div className="project-key-panel">
        <div className="project-key-header">
          {/* Search bar - hidden when empty */}
          <div className="project-key-search" style={{ visibility: 'hidden' }}>
            <div className="search-input-wrapper">
              <Search size={18} className="search-icon" />
              <input type="text" className="search-input" disabled />
            </div>
          </div>
        </div>

        <div className="project-key-content">
          <div className="component-list">
            {/* HIDDEN - Special Receptacles Card - Shows even when no project key */}
            {/* To re-enable: change false to true below */}
            {false && (
            <div 
              className={`component-card receptacles-special ${selectedForPlacement === 'RECEPTACLES' ? 'selected-for-placement' : ''}`}
              onClick={() => {
                if (hideExtraction && onComponentSelectForPlacement) {
                  if (selectedForPlacement === 'RECEPTACLES') {
                    onComponentSelectForPlacement(null);
                  } else {
                    onComponentSelectForPlacement({
                      name: 'RECEPTACLES',
                      symbol: 'Receptacle',
                      color: 'var(--cyan-text-dark)'
                    });
                  }
                }
              }}
              style={{ cursor: hideExtraction ? 'pointer' : 'default' }}
            >
              <div className="receptacles-header">
                <div className="receptacles-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" fill="white" stroke="var(--border-color)" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="receptacles-info">
                  <h3 className="receptacles-title">RECEPTACLES</h3>
                  <p className="receptacles-subtitle">
                    {selectedForPlacement === 'RECEPTACLES' 
                      ? '✓ Click on PDF to place receptacles' 
                      : hideExtraction 
                        ? 'Click to select, then place on PDF' 
                        : 'Auto-detection ready'}
                  </p>
                </div>
              </div>
              
              {/* Count display */}
              <div className="component-stats">
                {(() => {
                  const componentCounts = annotationCounts.get('RECEPTACLES');
                  let total = 0;
                  const pages: { page: number; count: number }[] = [];
                  let currentPageCount = 0;

                  if (componentCounts) {
                    componentCounts!.forEach((count, page) => {
                      total += count;
                      pages.push({ page, count });
                      if (page === currentPage) {
                        currentPageCount = count;
                      }
                    });
                    pages.sort((a, b) => a.page - b.page);
                  }

                  return (
                    <>
                      <div className="component-count-summary">
                        <div className="count-badge page-count-badge">
                          <span className="count-label">This Page:</span>
                          <span className="count-value">{currentPageCount}</span>
                        </div>
                        <div className="count-badge total-count-badge">
                          <span className="count-label">Total:</span>
                          <span className="count-value">{total}</span>
                        </div>
                      </div>
                      
                      {pages.length > 0 && (
                        <div className="component-pages">
                          <div className="page-pills">
                            {pages.map((page) => (
                              <span 
                                key={page.page} 
                                className={`page-pill ${page.page === currentPage ? 'current-page' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pdfAnnotatorRef?.current?.navigateToPage) {
                                    pdfAnnotatorRef.current.navigateToPage(page.page);
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                P{page.page}: {page.count}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              <button 
                className={`autocount-btn ${isAutocounting ? 'autocounting' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAutocount();
                }}
                disabled={isAutocounting}
              >
                {isAutocounting ? (
                  <>
                    <svg className="brain-pulse" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.5 2C8 2 7 3 7 4.5v.5c-1 0-2 1-2 2v.5c-1 0-2 1-2 2V13c0 1 1 2 2 2v1c0 1.5 1 2.5 2.5 2.5h.5c0 1 1 2 2 2h2c1 0 2-1 2-2h.5c1.5 0 2.5-1 2.5-2.5v-1c1 0 2-1 2-2V9.5c0-1-1-2-2-2V7c0-1-1-2-2-2v-.5c0-1.5-1-2.5-2.5-2.5h-5z" 
                            fill="currentColor" 
                            opacity="0.3"/>
                      <circle className="pulse-dot" cx="8" cy="10" r="1.5" fill="currentColor"/>
                      <circle className="pulse-dot pulse-delay-1" cx="12" cy="8" r="1.5" fill="currentColor"/>
                      <circle className="pulse-dot pulse-delay-2" cx="16" cy="10" r="1.5" fill="currentColor"/>
                      <circle className="pulse-dot pulse-delay-3" cx="12" cy="13" r="1.5" fill="currentColor"/>
                    </svg>
                    {autocountProgress || 'AI ANALYZING...'}
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.5 2C8 2 7 3 7 4.5v.5c-1 0-2 1-2 2v.5c-1 0-2 1-2 2V13c0 1 1 2 2 2v1c0 1.5 1 2.5 2.5 2.5h.5c0 1 1 2 2 2h2c1 0 2-1 2-2h.5c1.5 0 2.5-1 2.5-2.5v-1c1 0 2-1 2-2V9.5c0-1-1-2-2-2V7c0-1-1-2-2-2v-.5c0-1.5-1-2.5-2.5-2.5h-5z" 
                            stroke="currentColor" 
                            strokeWidth="1.5" 
                            fill="none"/>
                      <circle cx="8" cy="10" r="1.5" fill="currentColor"/>
                      <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
                      <path d="M9 14c0 0 1 2 3 2s3-2 3-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    AUTOCOUNT ALL RECEPTACLES
                  </>
                )}
              </button>
            </div>
            )}
          </div>

          {/* Empty state message */}
          <div className="project-key-empty-inline">
            <Key className="empty-icon-small" size={32} />
            <p className="empty-message-small">
              {hideExtraction ? 'No other components added yet' : 'No key extracted yet.'}
            </p>
            {!hideExtraction && (
              <button
                onClick={handleSelectKeyFromPDF}
                className={`select-key-btn-small ${isExtractingKey ? 'extracting' : ''}`}
                disabled={isExtractingKey}
                style={{ marginTop: '6px' }}
              >
                <Camera size={16} />
                {isExtractingKey ? 'Extracting...' : 'Extract Power Legend'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const lightingFixtures = projectKey.components.filter(
    (component: ComponentKey) => (component.symbolType || '') === 'Lighting'
  );
  const hasLightingFixtures = lightingFixtures.length > 0;

  return (
    <div className="project-key-panel">
      {/* Local backdrop when color picker is open */}
      {showColorPicker && (
        <div 
          className="project-key-backdrop" 
          onClick={() => {
            setShowColorPicker(null);
            setInlineColorPickerIndex(null);
            setInlinePreviewColor(null);
          }}
        />
      )}
      
      <div className="project-key-header">
        {/* Project Name - hide when in counting mode (hideExtraction) */}
        {projectName && !hideExtraction && (
          <div className="project-name-display">
            <span className="project-name-label">Project Name:</span>
            <span className="project-name-value">{projectName}</span>
          </div>
        )}
        
        {/* Search bar */}
        <div className="project-key-search">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="search-clear-btn"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
            <button
              className={`select-all-inline ${(() => {
              if (!projectKey) return '';
              const visibleKeys = projectKey.components
                .filter((c: any) => {
                  if (c.isStandardClass) return false;
                  if (filterSymbolType) {
                    if ((c.symbolType || '') !== filterSymbolType) return false;
                  } else {
                    if (c.symbolType === 'Lighting') return false;
                  }
                  if (!searchTerm) return true;
                  const s = searchTerm.toLowerCase();
                  return c.name.toLowerCase().includes(s) || c.symbol.toLowerCase().includes(s);
                })
                .map((c: any) => c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name);
              return visibleKeys.length > 0 && visibleKeys.every((k: string) => selectedComponents.has(k)) ? 'active' : '';
            })()}`}
            onClick={() => {
              if (!projectKey) return;
              const visibleKeys = projectKey.components
                .filter((c: any) => {
                  if (c.isStandardClass) return false;
                  if (filterSymbolType) {
                    if ((c.symbolType || '') !== filterSymbolType) return false;
                  } else {
                    if (c.symbolType === 'Lighting') return false;
                  }
                  if (!searchTerm) return true;
                  const s = searchTerm.toLowerCase();
                  return c.name.toLowerCase().includes(s) || c.symbol.toLowerCase().includes(s);
                })
                .map((c: any) => c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name);
              const allSelected = visibleKeys.length > 0 && visibleKeys.every((k: string) => selectedComponents.has(k));
              if (allSelected) {
                setSelectedComponents(new Set());
              } else {
                setSelectedComponents(new Set(visibleKeys));
              }
            }}
            title={(() => {
              if (!projectKey) return 'Select All';
              const visibleKeys = projectKey.components
                .filter((c: any) => {
                  if (c.isStandardClass) return false;
                  if (filterSymbolType) {
                    if ((c.symbolType || '') !== filterSymbolType) return false;
                  } else {
                    if (c.symbolType === 'Lighting') return false;
                  }
                  if (!searchTerm) return true;
                  const s = searchTerm.toLowerCase();
                  return c.name.toLowerCase().includes(s) || c.symbol.toLowerCase().includes(s);
                })
                .map((c: any) => c.symbolType === 'Lighting' ? (c.symbol || c.name) : c.name);
              return visibleKeys.length > 0 && visibleKeys.every((k: string) => selectedComponents.has(k)) ? 'Deselect All' : 'Select All';
            })()}
          >
              <CheckSquare size={18} />
            </button>
          </div>
        </div>

        {/* Bulk action bar - visible when any components are selected */}
        {selectedComponents.size > 0 && (
          <div className="bulk-action-bar">
            <span className="bulk-selection-count">
              {selectedComponents.size} selected
            </span>
            <div className="bulk-action-buttons">
              <button
                className="bulk-clear-btn"
                onClick={() => setSelectedComponents(new Set())}
              >
                Clear
              </button>
              <button
                className="bulk-delete-btn"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                <Trash2 size={14} />
                {isBulkDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
        {/* Add New Component - top right below search */}
        {!isAddingNew && (
          <div className="project-key-add-row">
            {filterSymbolType === 'Lighting' && hasLightingFixtures && (
              <button
                onClick={() => handleCountAll('LIGHTING FIXTURES', lightingFixtures)}
                className="count-all-lighting-btn"
                disabled={isAutocounting}
                title="Count all extracted lighting fixtures"
              >
                <Lightbulb size={18} />
                <span>{isAutocounting ? 'Counting...' : 'Count All Lighting'}</span>
              </button>
            )}
            {filterSymbolType === 'Lighting' && !hasLightingFixtures && (
              <button
                onClick={() => {
                  if (pdfAnnotatorRef?.current?.handleStartLightingScheduleExtraction) {
                    pdfAnnotatorRef.current.handleStartLightingScheduleExtraction();
                  }
                }}
                className="extract-lighting-btn"
                title="Extract lighting fixture schedule from PDF"
              >
                <Lightbulb size={16} />
                <span>Extract Lighting Schedule</span>
              </button>
            )}
            {!filterSymbolType && (
              <button
                onClick={handleCountAllPower}
                className="count-all-power-btn"
                disabled={isAutocounting}
                title="Count receptacles, telecom/data, and boxes"
              >
                <Zap size={18} />
                <span>Count All Power</span>
              </button>
            )}
            <button
              onClick={() => {
                if (filterSymbolType === 'Lighting') {
                  setIsAddingNew(true);
                } else if (pdfAnnotatorRef?.current?.openAddNewComponentModal) {
                  pdfAnnotatorRef.current.openAddNewComponentModal();
                } else {
                  setIsAddingNew(true);
                }
              }}
              className="add-component-btn-top"
            >
              <Plus size={18} />
              <span>{filterSymbolType === 'Lighting' ? 'Add New Fixture Type' : 'Add New Component'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Cost Breakdown Modal */}
      {showCostBreakdown && projectKey && (
        <CostBreakdown
          estimationId={estimationId}
          projectKey={projectKey}
          annotationCounts={annotationCounts}
          onClose={() => setShowCostBreakdown(false)}
        />
      )}

      <div className="project-key-content">
        {/* Add New Component form - at top when expanded */}
        {isAddingNew && (
          <div className="add-component-card">
            <div className="add-component-header">
              <div className="add-component-info">
                <div className="color-picker-wrapper" ref={showColorPicker === 'new' ? colorPickerRef : undefined} style={{ position: 'relative', zIndex: showColorPicker === 'new' ? 1002 : 'auto' }}>
                  <button
                    className="add-color-panel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowColorPicker(showColorPicker === 'new' ? null : 'new');
                    }}
                    style={{ backgroundColor: newComponent.color }}
                    title="Choose color"
                  />
                  {showColorPicker === 'new' && ReactDOM.createPortal(
                    <>
                      <div 
                        style={{ 
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0, 0, 0, 0.3)',
                          zIndex: 99998
                        }}
                        onClick={() => setShowColorPicker(null)}
                      />
                      <div style={{ 
                        position: 'fixed', 
                        zIndex: 99999, 
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}>
                        <ColorWheel
                          currentColor={newComponent.color}
                          onColorChange={(color) => setNewComponent({ ...newComponent, color, customColor: true })}
                          onClose={() => setShowColorPicker(null)}
                        />
                      </div>
                    </>,
                    document.body
                  )}
                </div>
                <textarea
                  placeholder="COMPONENT NAME"
                  value={newComponent.name}
                  onChange={(e) => {
                    const uppercase = e.target.value.toUpperCase();
                    setNewComponent({ ...newComponent, name: uppercase });
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  className="add-component-name-input"
                  autoFocus
                  rows={1}
                />
              </div>
            </div>
            <textarea
              placeholder="Symbol description"
              value={newComponent.symbol}
              onChange={(e) => {
                setNewComponent({ ...newComponent, symbol: e.target.value });
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              className="add-component-desc-input"
              rows={1}
            />
            <div className="add-component-actions">
              <button
                onClick={handleAddNew}
                className="add-action-btn save"
                disabled={!newComponent.name.trim() || !newComponent.symbol.trim()}
              >
                <Save size={14} />
                Save
              </button>
              <button
                onClick={() => {
                  setIsAddingNew(false);
                  setNewComponent({ color: 'rgb(255, 0, 0)', name: '', symbol: '', pages: [], totalCount: 0 });
                }}
                className="add-action-btn cancel"
              >
                <X size={14} />
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="component-list">
          {/* Regular Component list organized into folders */}
          {(() => {
            if (!projectKey) return null;
            
            const filteredComponents = projectKey.components
              .filter((component: any) => {
                // Filter by symbolType if specified
                if (filterSymbolType) {
                  if ((component.symbolType || '') !== filterSymbolType) return false;
                } else {
                  // When no filter, exclude Lighting components (they have their own tab)
                  if (component.symbolType === 'Lighting') return false;
                }
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return (
                  component.name.toLowerCase().includes(searchLower) ||
                  component.symbol.toLowerCase().includes(searchLower)
                );
              })
              .sort((a, b) => {
                // Get current page count for component A (use symbol for lighting, name for regular)
                const keyA = a.symbolType === 'Lighting' ? (a.symbol || a.name) : a.name;
                const countsA = annotationCounts.get(keyA);
                const countA = countsA?.get(currentPage) || 0;
                
                // Get current page count for component B
                const keyB = b.symbolType === 'Lighting' ? (b.symbol || b.name) : b.name;
                const countsB = annotationCounts.get(keyB);
                const countB = countsB?.get(currentPage) || 0;
                
                // Sort descending (highest count first)
                if (countB !== countA) {
                  return countB - countA;
                }
                
                // If counts are the same, maintain original order
                return projectKey.components.indexOf(a) - projectKey.components.indexOf(b);
              });

            const isLightingOnlyView = filterSymbolType === 'Lighting';
            const groups = isLightingOnlyView
              ? [
                  { id: 'LIGHTING', label: 'LIGHTING FIXTURES', items: filteredComponents }
                ]
              : [
                  { id: 'RECEPTACLES', label: 'RECEPTACLES', items: filteredComponents.filter(c => c.componentType === 'Receptacles' && !isSwitchComponent(c)) },
                  { id: 'TELECOM', label: 'TELECOM / DATA', items: filteredComponents.filter(c => c.componentType === 'Telecom' && !isSwitchComponent(c)) },
                  { id: 'BOXES', label: 'BOXES', items: filteredComponents.filter(c => isJunctionBoxComponent(c) && !isSwitchComponent(c)) },
                  { id: 'SWITCHES', label: 'SWITCHES', items: filteredComponents.filter(c => isSwitchComponent(c)) },
                  { id: 'OTHER', label: 'OTHER POWER COMPONENTS', items: filteredComponents.filter(c => c.componentType !== 'Receptacles' && c.componentType !== 'Telecom' && !isJunctionBoxComponent(c) && !isSwitchComponent(c)) }
                ];

            return groups.map(group => {
              if (group.items.length === 0) return null;
              const isExpanded = isLightingOnlyView || expandedFolders.has(group.id);

              return (
                <div key={group.id} className={isLightingOnlyView ? undefined : 'component-folder'}>
                  {!isLightingOnlyView && (
                    <div 
                      className="folder-header" 
                      onClick={() => {
                        const newExpanded = new Set(expandedFolders);
                        if (isExpanded) newExpanded.delete(group.id);
                        else newExpanded.add(group.id);
                        setExpandedFolders(newExpanded);
                      }}
                    >
                      <div className="folder-title">
                        <ChevronLeft 
                          size={18} 
                          style={{ 
                            transform: isExpanded ? 'rotate(-90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }} 
                        />
                        <span>{group.label}</span>
                      </div>
                      <div className="folder-header-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="folder-count-all-btn"
                          onClick={() => handleCountAll(group.id, group.items)}
                          disabled={isAutocounting}
                          title={`Count all ${group.label.toLowerCase()}`}
                        >
                          <Sparkles size={12} />
                          <span>Count All</span>
                        </button>
                        <span className="folder-count">{group.items.length}</span>
                      </div>
                    </div>
                  )}
                  
                  {isExpanded && (
                    <div className="folder-content">
                      {group.items.map((component, index) => {
                        // Get the original index for editing purposes
                        const originalIndex = projectKey.components.indexOf(component);
                        // Use symbol as selection key for lighting (unique per fixture), name for regular components
                        const selectionKey = component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name;
                        return (
                          <div
                            key={selectionKey + '-' + index}
                            className={`component-card ${highlightedComponentName === selectionKey ? 'highlighted' : ''} ${filteredComponent === selectionKey ? 'filtered' : ''} ${selectedForPlacement === selectionKey ? 'selected-for-placement' : ''} ${selectedComponents.has(selectionKey) ? 'bulk-selected' : ''}`}
                            style={{ '--component-color': component.color } as React.CSSProperties}
                            draggable={!editingIndex && !showColorPicker && !hideExtraction}
                            onDragStart={(e) => {
                              if (!editingIndex && !showColorPicker) {
                                e.dataTransfer.setData('component', JSON.stringify({
                                  name: component.name,
                                  symbol: component.symbol,
                                  color: component.color,
                                  symbolType: component.symbolType
                                }));
                                e.dataTransfer.effectAllowed = 'copy';
                              }
                            }}
                            onClick={() => {
                              if (!editingIndex && !showColorPicker) {
                                if (hideExtraction && onComponentSelectForPlacement) {
                                  if (selectedForPlacement === selectionKey) {
                                    onComponentSelectForPlacement(null);
                                  } else {
                                    onComponentSelectForPlacement({
                                      name: component.name,
                                      symbol: component.symbol,
                                      color: component.color,
                                      symbolType: component.symbolType
                                    });
                                  }
                                  if (onFilteredComponentChange) {
                                    onFilteredComponentChange(selectionKey);
                                  }
                                  setHighlightedComponentName(selectionKey);
                                  if (pdfAnnotatorRef?.current?.highlightComponentType) {
                                    pdfAnnotatorRef.current.highlightComponentType(selectionKey);
                                  }
                                  return;
                                }
                                const newFilter = selectionKey;
                                if (onFilteredComponentChange) {
                                  onFilteredComponentChange(newFilter);
                                }
                                setHighlightedComponentName(newFilter);
                                if (pdfAnnotatorRef?.current?.highlightComponentType) {
                                  pdfAnnotatorRef.current.highlightComponentType(newFilter);
                                }
                                if (onComponentSelect) {
                                  onComponentSelect(component);
                                }
                              }
                            }}
                          >
                            {editingIndex === originalIndex ? (
                              <div>
                                <div className="component-header">
                                  <div className="component-info">
                                    <div className="color-picker-wrapper" ref={showColorPicker === 'edit' ? colorPickerRef : undefined} style={{ position: 'relative' }}>
                                      <button
                                        className="component-color"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowColorPicker(showColorPicker === 'edit' ? null : 'edit');
                                        }}
                                        style={{ backgroundColor: editedComponent!.color, cursor: 'pointer' }}
                                      />
                                      {showColorPicker === 'edit' && ReactDOM.createPortal(
                                        <>
                                          <div 
                                            style={{ 
                                              position: 'fixed',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              background: 'rgba(0, 0, 0, 0.3)',
                                              zIndex: 99998
                                            }}
                                            onClick={() => setShowColorPicker(null)}
                                          />
                                          <div style={{ 
                                            position: 'fixed',
                                            zIndex: 99999,
                                            left: '50%',
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)'
                                          }}>
                                            <ColorWheel
                                              currentColor={editedComponent!.color}
                                              onColorChange={(color) => setEditedComponent({ ...editedComponent!, color, customColor: true })}
                                              onClose={() => setShowColorPicker(null)}
                                            />
                                          </div>
                                        </>,
                                        document.body
                                      )}
                                    </div>
                                    <textarea
                                      ref={editNameRef}
                                      value={editedComponent!.name}
                                      onChange={(e) => {
                                        const target = e.target;
                                        const start = target.selectionStart;
                                        const end = target.selectionEnd;
                                        const uppercase = target.value.toUpperCase();
                                        setEditedComponent({ ...editedComponent!, name: uppercase });
                                        requestAnimationFrame(() => {
                                          target.selectionStart = start;
                                          target.selectionEnd = end;
                                        });
                                        target.style.height = 'auto';
                                        target.style.height = target.scrollHeight + 'px';
                                      }}
                                      className="add-component-name-input"
                                      onClick={(e) => e.stopPropagation()}
                                      rows={1}
                                    />
                                  </div>
                                </div>
                                <textarea
                                  ref={editSymbolRef}
                                  value={editedComponent!.symbol}
                                  onChange={(e) => {
                                    setEditedComponent({ ...editedComponent!, symbol: e.target.value });
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  className="add-component-desc-input"
                                  placeholder="Symbol description"
                                  onClick={(e) => e.stopPropagation()}
                                  rows={1}
                                />
                                <div className="add-component-actions">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSave();
                                    }}
                                    className="add-action-btn save"
                                  >
                                    <Save size={16} />
                                    Save
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancel();
                                    }}
                                    className="add-action-btn cancel"
                                  >
                                    <X size={16} />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="component-header">
                                  <div className="component-info">
                                    {!component.isStandardClass && (
                                      <button
                                        className="component-select-checkbox"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const selectionKey = component.symbolType === 'Lighting' ? (component.symbol || component.name) : component.name;
                                          const newSelected = new Set(selectedComponents);
                                          if (newSelected.has(selectionKey)) {
                                            newSelected.delete(selectionKey);
                                          } else {
                                            newSelected.add(selectionKey);
                                          }
                                          setSelectedComponents(newSelected);
                                        }}
                                      >
                                        {selectedComponents.has(selectionKey) ? (
                                          <CheckSquare size={18} className="checkbox-checked" />
                                        ) : (
                                          <Square size={18} className="checkbox-unchecked" />
                                        )}
                                      </button>
                                    )}
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        className="component-color"
                                        style={{
                                          backgroundColor: (showColorPicker === 'inline' && inlineColorPickerIndex === originalIndex && inlinePreviewColor)
                                            ? inlinePreviewColor
                                            : component.color,
                                          cursor: 'pointer',
                                          border: 'none'
                                        }}
                                        title="Change color"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (showColorPicker === 'inline' && inlineColorPickerIndex === originalIndex) {
                                            handleInlineColorDismiss();
                                          } else {
                                            setShowColorPicker('inline');
                                            setInlineColorPickerIndex(originalIndex);
                                            setInlinePreviewColor(null);
                                          }
                                        }}
                                      />
                                      {showColorPicker === 'inline' && inlineColorPickerIndex === originalIndex && ReactDOM.createPortal(
                                        <>
                                          <div 
                                            style={{ 
                                              position: 'fixed',
                                              top: 0, left: 0, right: 0, bottom: 0,
                                              background: 'rgba(0, 0, 0, 0.3)',
                                              zIndex: 99998
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleInlineColorDismiss();
                                            }}
                                          />
                                          <div style={{ 
                                            position: 'fixed',
                                            zIndex: 99999,
                                            left: '50%',
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)'
                                          }}>
                                            <ColorWheel
                                              currentColor={inlinePreviewColor || component.color}
                                              onColorChange={(color) => setInlinePreviewColor(color)}
                                              onClose={handleInlineColorDismiss}
                                            />
                                            <div style={{
                                              display: 'flex',
                                              gap: '8px',
                                              justifyContent: 'center',
                                              marginTop: '8px'
                                            }}>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handleInlineColorConfirm(); }}
                                                style={{
                                                  padding: '6px 20px',
                                                  borderRadius: '8px',
                                                  border: 'none',
                                                  background: '#3b82f6',
                                                  color: '#ffffff',
                                                  fontWeight: 600,
                                                  fontSize: '13px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                Apply
                                              </button>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handleInlineColorDismiss(); }}
                                                style={{
                                                  padding: '6px 20px',
                                                  borderRadius: '8px',
                                                  border: '1px solid var(--border-strong)',
                                                  background: 'var(--bg-primary)',
                                                  color: 'var(--text-secondary)',
                                                  fontWeight: 600,
                                                  fontSize: '13px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        </>,
                                        document.body
                                      )}
                                    </div>
                                    <span className="component-name">{component.name}</span>
                                  </div>
                                  <div className="component-header-right">
                                    {(componentImages.get(component.name) || component.referenceImage) && (
                                      <div className="component-reference-image">
                                        <img 
                                          src={componentImages.get(component.name) || component.referenceImage} 
                                          alt={`${component.name} reference`}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {component.symbol && (
                                  <div className="component-symbol">
                                    {component.symbol}
                                  </div>
                                )}
                                {component.description && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'var(--text-muted)',
                                    lineHeight: '1.4',
                                    padding: '4px 0 2px',
                                    wordBreak: 'break-word'
                                  }}>
                                    {component.description}
                                  </div>
                                )}
                                <div className="component-stats">
                                  {(() => {
                                    const componentCounts = annotationCounts.get(selectionKey);
                                    let total = 0;
                                    const pages: { page: number; count: number }[] = [];
                                    let currentPageCount = 0;

                                    if (componentCounts) {
                                      componentCounts.forEach((count, page) => {
                                        total += count;
                                        pages.push({ page, count });
                                        if (page === currentPage) {
                                          currentPageCount = count;
                                        }
                                      });
                                      pages.sort((a, b) => a.page - b.page);
                                    }

                                    return (
                                      <>
                                        <div className="component-count-summary">
                                          <div className="count-badge page-count-badge">
                                            <span className="count-label">This Page:</span>
                                            <span className="count-value">{currentPageCount}</span>
                                          </div>
                                          <div className="count-badge total-count-badge">
                                            <span className="count-label">Total:</span>
                                            <span className="count-value">{total}</span>
                                          </div>
                                        </div>
                                        
                                        {pages.length > 0 && (
                                          <div className="component-pages">
                                            <div className="page-pills">
                                              {pages.map((page) => (
                                                <span 
                                                  key={page.page} 
                                                  className={`page-pill ${page.page === currentPage ? 'current-page' : ''}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (pdfAnnotatorRef?.current?.navigateToPage) {
                                                      pdfAnnotatorRef.current.navigateToPage(page.page);
                                                    }
                                                  }}
                                                  style={{ cursor: 'pointer' }}
                                                >
                                                  P{page.page}: {page.count}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <div className="component-actions">
                                  {!component.isStandardClass && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(originalIndex);
                                        }}
                                        className="component-action-btn"
                                        title="Edit component"
                                      >
                                        <Edit2 size={14} />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(originalIndex);
                                        }}
                                        className="component-action-btn delete"
                                        title="Delete component"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>

        {/* Update Key from PDF button */}
        {!hideExtraction && (
          <button
            onClick={handleSelectKeyFromPDF}
            className={`update-key-btn-list ${isExtractingKey ? 'extracting' : ''}`}
            disabled={isExtractingKey}
            title="Update key by selecting from PDF"
          >
            <Camera size={18} />
            <div className="update-key-content">
              <span className="update-key-label">{isExtractingKey ? 'Extracting Key...' : 'Update Key from PDF'}</span>
              {isExtractingKey && extractionProgress && (
                <span className="update-key-progress">{extractionProgress}</span>
              )}
            </div>
          </button>
        )}
      </div>

    </div>
  );
};